// Rate plan editor (SCR-008, RatePlan.dc.html): header with the plan's name and status and its
// actions; "About this plan"; "Pricing" (what the markup applies to, markup %, monthly minimum,
// platform fee); "Model exceptions" (per-model / token-class markup or fixed price); "Volume
// discounts" (graduated tiers); and the "What a customer pays" aside. Drafts are editable by
// billing writers; active and retired plans (and every plan for a billing viewer) are read-only,
// with Clone as the way to change one. Lifecycle: Save (create / PATCH), Activate, Retire, Clone.
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import type { ApiError } from "@nexus/ui";
import { RouterContext } from "../../router/router";
import { SessionContext } from "../../session/session";
import { Field, InputAffix, LabelText, errorId } from "@nexus/ui";
import { HelpButton } from "@nexus/ui";
import { actorDisplay, actorTitle, knownActor } from "../../ui/identity";
import { Icon } from "@nexus/ui";
import { parsePercentToBp } from "@nexus/ui";
import { Card, Chip, PageHeader, Segmented } from "@nexus/ui";
import { useBreadcrumb } from "../../ui/shell";
import { ErrorBanner, Loading } from "@nexus/ui";
import { formatDay } from "../billing/model";
import {
  MARKUP_BASIS_OPTIONS,
  PLAN_CONFLICT_MESSAGE,
  TOKEN_CLASS_OPTIONS,
  addRateRow,
  addTierRow,
  buildPatchRatePlanRequest,
  buildRatePlanInput,
  cloneToForm,
  emptyPlanForm,
  formIsDirty,
  isPlanConflict,
  loadRatePlan,
  marginLabel,
  markupBasisLabel,
  planActions,
  planFormFromPlan,
  planFormIsValid,
  planStatusView,
  removeRateRow,
  removeTierRow,
  tokenClassLabel,
  updateRateRow,
  updateTierRow,
  validatePlanForm,
  type PlanFormValues,
  type RatePlan,
  type TokenClassSelector,
} from "./model";
import { WorkedExample } from "./worked-example";

type Loaded = { plan: RatePlan | null; form: PlanFormValues };

/** An inline error under a table cell's control. */
function CellError({ id, message }: { id: string; message: string | undefined }) {
  return message ? (
    <p class="field-error" id={id} role="alert">
      {message}
    </p>
  ) : null;
}

function describedBy(id: string, message: string | undefined): string | undefined {
  return message ? id : undefined;
}

export function PlanEditor({ params }: { params: Record<string, string>; query: URLSearchParams }) {
  const client = useApi();
  const services = useContext(SessionContext);
  const router = useContext(RouterContext);
  const planId = params.planId === "new" ? null : (params.planId ?? null);

  const session = services?.state.status === "signed-in" ? services.state.session : null;
  const canWrite = session?.can("billing:write") ?? false;

  const [loaded, setLoaded] = useState<Loaded | null>(planId ? null : { plan: null, form: emptyPlanForm() });
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  useBreadcrumb(loaded ? [{ label: loaded.plan ? loaded.plan.name : "New rate plan" }] : null);

  useEffect(() => {
    if (!planId || !client) return;
    setLoaded(null);
    void loadRatePlan(client, planId).then((state) => {
      if (state.kind === "loaded") setLoaded({ plan: state.plan, form: planFormFromPlan(state.plan) });
      else if (state.kind === "error") setError(state.error);
    });
  }, [planId, client]);

  if (planId && !loaded) return error ? <ErrorBanner error={error} /> : <Loading />;
  if (!loaded) return <Loading />;

  const { plan, form } = loaded;
  const setForm = (next: PlanFormValues) => setLoaded({ plan, form: next });
  const errors = validatePlanForm(form);
  const actions = plan ? planActions(plan, canWrite) : { canEdit: canWrite, canActivate: false, canRetire: false, canClone: false };
  const editable = actions.canEdit || !plan;
  const dirty = plan ? formIsDirty(plan, form) : true;

  const save = async () => {
    if (!client || !planFormIsValid(form)) return;
    setBusy(true);
    setError(null);
    try {
      const saved = plan
        ? await client.patch("/v1/admin/rate-plans/{planId}", { path: { planId: plan.id }, body: buildPatchRatePlanRequest(form) })
        : await client.post("/v1/admin/rate-plans", { body: buildRatePlanInput(form) });
      setLoaded({ plan: saved, form: planFormFromPlan(saved) });
      router?.navigate(`/rate-plans/${saved.id}`, { replace: true });
    } catch (e) {
      const apiError = e as ApiError;
      setError(apiError);
      // Another request saved first (409 `conflict`): the draft this editor holds is stale, so
      // reload the plan it overtook rather than let Save fail again the same way.
      if (plan && isPlanConflict(apiError)) {
        void loadRatePlan(client, plan.id).then((state) => {
          if (state.kind === "loaded") setLoaded({ plan: state.plan, form: planFormFromPlan(state.plan) });
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const lifecycle = async (action: "activate" | "retire") => {
    if (!client || !plan) return;
    setBusy(true);
    setError(null);
    try {
      const saved =
        action === "activate"
          ? await client.post("/v1/admin/rate-plans/{planId}/activate", { path: { planId: plan.id } })
          : await client.post("/v1/admin/rate-plans/{planId}/retire", { path: { planId: plan.id } });
      setLoaded({ plan: saved, form: planFormFromPlan(saved) });
    } catch (e) {
      setError(e as ApiError);
    } finally {
      setBusy(false);
    }
  };

  const clone = () => {
    if (!plan) return;
    setError(null);
    router?.navigate("/rate-plans/new");
    setLoaded({ plan: null, form: cloneToForm(plan) });
  };

  const cancel = () => router?.navigate("/rate-plans");

  const status = plan ? planStatusView(plan.status) : null;
  const markupBp = parsePercentToBp(form.markupPercent);
  const markupDisplay = markupBp === null ? "the plan" : `the ${form.markupPercent.trim()} %`;

  const headerActions = (
    <>
      {editable && (
        <button type="button" class="button button-secondary" onClick={cancel}>
          Cancel
        </button>
      )}
      {actions.canActivate && (
        <button
          type="button"
          class="button"
          disabled={busy || dirty}
          title={dirty ? "Save your changes first; Activate freezes the saved plan." : undefined}
          onClick={() => void lifecycle("activate")}
        >
          Activate
        </button>
      )}
      {actions.canClone && (
        <button type="button" class="button" onClick={clone}>
          Clone
        </button>
      )}
      {actions.canRetire && (
        <button type="button" class="button button-danger" disabled={busy} onClick={() => void lifecycle("retire")}>
          Retire
        </button>
      )}
      {editable && (
        <button type="button" class="button button-primary" disabled={busy || !planFormIsValid(form)} onClick={() => void save()}>
          {busy ? "Saving…" : plan ? "Save changes" : "Save draft"}
        </button>
      )}
    </>
  );

  const meta = plan ? (
    <span class="muted">
      Created {formatDay(plan.createdAt)} by <span title={actorTitle(plan.createdBy, knownActor(plan, "createdBy"))}>{actorDisplay(plan.createdBy, session?.me, knownActor(plan, "createdBy"))}</span>
      {plan.activatedAt ? ` · activated ${formatDay(plan.activatedAt)}` : ""}
    </span>
  ) : undefined;

  const fieldError = (key: string) => errors[key];

  return (
    <div class="page plan-editor">
      <PageHeader
        title={plan ? plan.name : "New rate plan"}
        titleExtra={status && <Chip tone={status.tone}>{status.label}</Chip>}
        lede="A rate plan turns a customer's AWS usage into the price on their invoice."
        meta={meta}
        actions={headerActions}
      />

      {error && isPlanConflict(error) && (
        <div class="banner banner-error" role="alert">
          <p>{PLAN_CONFLICT_MESSAGE}</p>
        </div>
      )}
      {error && !isPlanConflict(error) && <ErrorBanner error={error} onRetry={() => setError(null)} />}
      {!editable && plan && plan.status !== "draft" && (
        <div class="banner banner-info">
          <p>
            Pricing fields are frozen once a plan leaves draft.
            {actions.canClone ? " Clone it to make a changed copy you can assign from a later month." : ""}
          </p>
        </div>
      )}
      {!editable && plan && plan.status === "draft" && (
        <div class="banner banner-info">
          <p>You can read this draft; only a billing admin can change it.</p>
        </div>
      )}
      {actions.canActivate && dirty && (
        <p class="field-hint">Save your changes before activating: activating freezes the saved plan.</p>
      )}

      <div class="split plan-layout">
        <div class="stack">
          <Card title="About this plan">
            <div class="form-grid plan-about">
              <Field id="plan-name" label="Plan name" help="rateplan.name" error={fieldError("name")}>
                <input
                  id="plan-name"
                  type="text"
                  disabled={!editable}
                  value={form.name}
                  aria-invalid={Boolean(fieldError("name"))}
                  aria-describedby={describedBy(errorId("plan-name"), fieldError("name"))}
                  onInput={(e) => setForm({ ...form, name: e.currentTarget.value })}
                />
              </Field>
              <Field id="plan-description" label="Description" help="rateplan.description" error={fieldError("description")}>
                <input
                  id="plan-description"
                  type="text"
                  disabled={!editable}
                  value={form.description}
                  aria-invalid={Boolean(fieldError("description"))}
                  aria-describedby={describedBy(errorId("plan-description"), fieldError("description"))}
                  onInput={(e) => setForm({ ...form, description: e.currentTarget.value })}
                />
              </Field>
            </div>
          </Card>

          <Card title="Pricing">
            <div class="form-grid plan-pricing">
              <div class="field">
                <LabelText id="plan-markup-basis-label" label="Markup applies to" help="rateplan.markupBasis" />
                {editable ? (
                  <Segmented
                    options={MARKUP_BASIS_OPTIONS}
                    value={form.markupBasis}
                    labelledBy="plan-markup-basis-label"
                    onChange={(markupBasis) => setForm({ ...form, markupBasis })}
                  />
                ) : (
                  <span class="plan-static" aria-labelledby="plan-markup-basis-label">
                    {markupBasisLabel(form.markupBasis)}
                  </span>
                )}
                {form.markupBasis === "actual" && (
                  <span class="field-hint">
                    A customer on the actual AWS bill is invoiced only once that month's AWS bill (Cost and Usage Report) is finalized.
                  </span>
                )}
              </div>
              <Field
                id="plan-markup"
                label="Markup"
                help="rateplan.markup"
                error={fieldError("markupBp")}
                hint={markupBp === null ? undefined : marginLabel(markupBp)}
              >
                <InputAffix suffix="%">
                  <input
                    id="plan-markup"
                    type="text"
                    inputMode="decimal"
                    disabled={!editable}
                    value={form.markupPercent}
                    aria-invalid={Boolean(fieldError("markupBp"))}
                    aria-describedby={describedBy(errorId("plan-markup"), fieldError("markupBp"))}
                    onInput={(e) => setForm({ ...form, markupPercent: e.currentTarget.value })}
                  />
                </InputAffix>
              </Field>
              <Field id="plan-minimum" label="Monthly minimum" help="rateplan.monthlyMinimum" error={fieldError("monthlyMinimumUsd")}>
                <InputAffix prefix="$">
                  <input
                    id="plan-minimum"
                    type="text"
                    inputMode="decimal"
                    disabled={!editable}
                    value={form.monthlyMinimumUsd}
                    aria-invalid={Boolean(fieldError("monthlyMinimumUsd"))}
                    aria-describedby={describedBy(errorId("plan-minimum"), fieldError("monthlyMinimumUsd"))}
                    onInput={(e) => setForm({ ...form, monthlyMinimumUsd: e.currentTarget.value })}
                  />
                </InputAffix>
              </Field>
              <Field id="plan-fee" label="Platform fee per month" help="rateplan.platformFee" error={fieldError("platformFeeUsd")}>
                <InputAffix prefix="$">
                  <input
                    id="plan-fee"
                    type="text"
                    inputMode="decimal"
                    disabled={!editable}
                    value={form.platformFeeUsd}
                    aria-invalid={Boolean(fieldError("platformFeeUsd"))}
                    aria-describedby={describedBy(errorId("plan-fee"), fieldError("platformFeeUsd"))}
                    onInput={(e) => setForm({ ...form, platformFeeUsd: e.currentTarget.value })}
                  />
                </InputAffix>
              </Field>
            </div>
          </Card>

          <Card
            title="Model exceptions"
            help="rateplan.exceptions"
            actions={
              editable && (
                <button type="button" class="button button-sm" onClick={() => setForm({ ...form, rates: addRateRow(form.rates) })}>
                  <Icon name="plus" small />
                  Add exception
                </button>
              )
            }
          >
            {form.rates.length === 0 ? (
              <p class="muted">None. Every model uses {markupDisplay} markup above.</p>
            ) : (
              <div class="table-scroll">
                <table class="data-table plan-rows rates-table">
                  <caption class="visually-hidden">Model exceptions</caption>
                  <thead>
                    <tr>
                      <th scope="col">
                        <span class="th-inner">
                          Model
                          <HelpButton topic="rateplan.exceptionModel" label="Model" />
                        </span>
                      </th>
                      <th scope="col">
                        <span class="th-inner">
                          Token class
                          <HelpButton topic="rateplan.exceptionTokenClass" label="Token class" />
                        </span>
                      </th>
                      <th scope="col">
                        <span class="th-inner">
                          Price
                          <HelpButton topic="rateplan.exceptionPrice" label="Price" />
                        </span>
                      </th>
                      <th scope="col">Amount</th>
                      {editable && (
                        <th scope="col">
                          <span class="visually-hidden">Remove</span>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {form.rates.map((rate, i) => {
                      const modelError = fieldError(`rates.${i}.model`);
                      const valueError = fieldError(`rates.${i}.markupBp`) ?? fieldError(`rates.${i}.fixedPerM`);
                      return (
                        <tr key={i}>
                          <td>
                            <input
                              type="text"
                              class="input input-mono"
                              disabled={!editable}
                              value={rate.model}
                              placeholder="* or a Bedrock model id"
                              aria-label={`Exception ${i + 1}: model`}
                              aria-invalid={Boolean(modelError)}
                              aria-describedby={describedBy(`rate-${i}-model-error`, modelError)}
                              onInput={(e) => setForm({ ...form, rates: updateRateRow(form.rates, i, { model: e.currentTarget.value }) })}
                            />
                            <CellError id={`rate-${i}-model-error`} message={modelError} />
                          </td>
                          <td>
                            <select
                              class="input"
                              disabled={!editable}
                              value={rate.tokenClass}
                              aria-label={`Exception ${i + 1}: token class`}
                              onChange={(e) =>
                                setForm({ ...form, rates: updateRateRow(form.rates, i, { tokenClass: e.currentTarget.value as TokenClassSelector }) })
                              }
                            >
                              {TOKEN_CLASS_OPTIONS.map((tc) => (
                                <option key={tc} value={tc}>
                                  {tokenClassLabel(tc)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              class="input"
                              disabled={!editable}
                              value={rate.rateType}
                              aria-label={`Exception ${i + 1}: price as`}
                              onChange={(e) =>
                                setForm({ ...form, rates: updateRateRow(form.rates, i, { rateType: e.currentTarget.value as "markup" | "fixed" }) })
                              }
                            >
                              <option value="markup">Markup %</option>
                              <option value="fixed">Fixed $ per million tokens</option>
                            </select>
                          </td>
                          <td>
                            {rate.rateType === "markup" ? (
                              <InputAffix suffix="%">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  disabled={!editable}
                                  value={rate.markupPercent}
                                  aria-label={`Exception ${i + 1}: markup (%)`}
                                  aria-invalid={Boolean(valueError)}
                                  aria-describedby={describedBy(`rate-${i}-value-error`, valueError)}
                                  onInput={(e) => setForm({ ...form, rates: updateRateRow(form.rates, i, { markupPercent: e.currentTarget.value }) })}
                                />
                              </InputAffix>
                            ) : (
                              <InputAffix prefix="$" suffix="/ 1M">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  disabled={!editable}
                                  value={rate.fixedPerMUsd}
                                  aria-label={`Exception ${i + 1}: fixed price ($ per million tokens)`}
                                  aria-invalid={Boolean(valueError)}
                                  aria-describedby={describedBy(`rate-${i}-value-error`, valueError)}
                                  onInput={(e) => setForm({ ...form, rates: updateRateRow(form.rates, i, { fixedPerMUsd: e.currentTarget.value }) })}
                                />
                              </InputAffix>
                            )}
                            <CellError id={`rate-${i}-value-error`} message={valueError} />
                          </td>
                          {editable && (
                            <td class="numeric">
                              <button
                                type="button"
                                class="button button-sm button-ghost"
                                aria-label={`Remove exception ${i + 1}`}
                                onClick={() => setForm({ ...form, rates: removeRateRow(form.rates, i) })}
                              >
                                Remove
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card
            title="Volume discounts"
            help="rateplan.tiers"
            actions={
              editable && (
                <button type="button" class="button button-sm" onClick={() => setForm({ ...form, tiers: addTierRow(form.tiers) })}>
                  <Icon name="plus" small />
                  Add tier
                </button>
              )
            }
          >
            {form.tiers.length === 0 ? (
              <p class="muted">None. Every dollar of usage is charged at the full price.</p>
            ) : (
              <div class="table-scroll">
                <table class="data-table plan-rows tiers-table">
                  <caption class="visually-hidden">Volume discounts (graduated)</caption>
                  <thead>
                    <tr>
                      <th scope="col">
                        <span class="th-inner">
                          From monthly spend
                          <HelpButton topic="rateplan.tierFrom" label="From monthly spend" />
                        </span>
                      </th>
                      <th scope="col">
                        <span class="th-inner">
                          Discount
                          <HelpButton topic="rateplan.tierDiscount" label="Discount" />
                        </span>
                      </th>
                      {editable && (
                        <th scope="col">
                          <span class="visually-hidden">Remove</span>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {form.tiers.map((tier, i) => {
                      const fromError = fieldError(`tiers.${i}.fromMicroUsd`);
                      const discountError = fieldError(`tiers.${i}.discountBp`);
                      return (
                        <tr key={i}>
                          <td>
                            <InputAffix prefix="$">
                              <input
                                type="text"
                                inputMode="decimal"
                                disabled={!editable}
                                value={tier.fromUsd}
                                aria-label={`Tier ${i + 1}: from monthly spend ($)`}
                                aria-invalid={Boolean(fromError)}
                                aria-describedby={describedBy(`tier-${i}-from-error`, fromError)}
                                onInput={(e) => setForm({ ...form, tiers: updateTierRow(form.tiers, i, { fromUsd: e.currentTarget.value }) })}
                              />
                            </InputAffix>
                            <CellError id={`tier-${i}-from-error`} message={fromError} />
                          </td>
                          <td>
                            <InputAffix suffix="%">
                              <input
                                type="text"
                                inputMode="decimal"
                                disabled={!editable}
                                value={tier.discountPercent}
                                aria-label={`Tier ${i + 1}: discount (%)`}
                                aria-invalid={Boolean(discountError)}
                                aria-describedby={describedBy(`tier-${i}-discount-error`, discountError)}
                                onInput={(e) => setForm({ ...form, tiers: updateTierRow(form.tiers, i, { discountPercent: e.currentTarget.value }) })}
                              />
                            </InputAffix>
                            <CellError id={`tier-${i}-discount-error`} message={discountError} />
                          </td>
                          {editable && (
                            <td class="numeric">
                              <button
                                type="button"
                                class="button button-sm button-ghost"
                                aria-label={`Remove tier ${i + 1}`}
                                onClick={() => setForm({ ...form, tiers: removeTierRow(form.tiers, i) })}
                              >
                                Remove
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <div class="plan-aside">
          <WorkedExample values={form} />
        </div>
      </div>
    </div>
  );
}
